import React, {useEffect, useRef, useState} from 'react'
import PageLayout from '../../../components/layout/PageLayout'
import Text from '../../../components/text/Text'
import UnderLineInput from '../../../components/input/UnderLineInput'
import WizButton from '../../../components/button/WizButton'
import WizSelectButton from '../../../components/button/WizSelectButton'
import WizImage from '../../../components/image/WizImage'
import {postUploadImage} from '../../../apis/image/postUploadImage'
import {editBadge} from '../../../apis/badge/editBadge'
import {deleteBadge} from '../../../apis/badge/deleteBadge'
import {grantBadge} from '../../../apis/badge/grantBadge'
import {revokeBadge} from '../../../apis/badge/revokeBadge'
import {BADGE_CATEGORIES, BADGE_TYPES} from '../../../apis/badge/getBadge'
import useQueryBadge from '../../../hooks/api/badge/useQueryBadge'
import useNavigation from '../../../hooks/useNavigation'
import {useSearchParams} from 'react-router-dom'

interface IProps {
    className?: string
}

const categoryOptions = BADGE_CATEGORIES.map(c => ({id: c, value: c}))
const typeOptions = BADGE_TYPES.map(t => ({id: t, value: t}))

const BadgeDetailPage: React.FC<IProps> = ({className}) => {
    const [searchParams] = useSearchParams(document.location.search)
    const badgeId = searchParams.get('id')
    const {goBadge} = useNavigation()
    const {data} = useQueryBadge(badgeId)

    const [code, setCode] = useState('')
    const [category, setCategory] = useState('')
    const [type, setType] = useState('')
    const [nameEn, setNameEn] = useState('')
    const [nameKo, setNameKo] = useState('')
    const [descEn, setDescEn] = useState('')
    const [descKo, setDescKo] = useState('')
    const [criteriaEn, setCriteriaEn] = useState('')
    const [criteriaKo, setCriteriaKo] = useState('')
    const [requirementEn, setRequirementEn] = useState('')
    const [requirementKo, setRequirementKo] = useState('')
    const [image, setImage] = useState('')
    const [imageMini, setImageMini] = useState('')
    const [imageDisabled, setImageDisabled] = useState('')
    const [sortOrder, setSortOrder] = useState(0)
    const [remark, setRemark] = useState('')
    const [userIdsText, setUserIdsText] = useState('')

    const imageRef = useRef<any>()
    const imageMiniRef = useRef<any>()
    const imageDisabledRef = useRef<any>()

    useEffect(() => {
        const badge = data?.data
        if (badge) {
            setCode(badge.code || '')
            setCategory(badge.category || '')
            setType(badge.type || '')
            setNameEn(badge.name_i18n?.en || '')
            setNameKo(badge.name_i18n?.ko || '')
            setDescEn(badge.description_i18n?.en || '')
            setDescKo(badge.description_i18n?.ko || '')
            setCriteriaEn(badge.criteria_i18n?.en || '')
            setCriteriaKo(badge.criteria_i18n?.ko || '')
            setRequirementEn(badge.requirement_i18n?.en || '')
            setRequirementKo(badge.requirement_i18n?.ko || '')
            setImage(badge.image || '')
            setImageMini(badge.image_mini || '')
            setImageDisabled(badge.image_disabled || '')
            setSortOrder(badge.sort_order || 0)
            setRemark(badge.remark || '')
        }
    }, [data])

    const onChangeImage = async (e: any, setFn: (url: string) => void) => {
        const file = e?.target?.files[0]
        if (file) {
            const result = await postUploadImage(file)
            if (result?.image_url) {
                setFn(result.image_url)
            }
        }
    }

    const parseUserIds = (): string[] => {
        return userIdsText
            .split(/[\n,]+/)
            .map(s => s.trim())
            .filter(s => s.length > 0)
    }

    const onClickSave = async () => {
        if (!badgeId) return

        const body = {
            code,
            category,
            type,
            name_i18n: {en: nameEn, ko: nameKo},
            description_i18n: {en: descEn, ko: descKo},
            criteria_i18n: {en: criteriaEn, ko: criteriaKo},
            requirement_i18n: {en: requirementEn, ko: requirementKo},
            image,
            image_mini: imageMini,
            image_disabled: imageDisabled,
            is_active: true,
            sort_order: sortOrder,
            remark: remark || undefined,
        }

        const {data, error} = await editBadge(badgeId, body)
        if (data) {
            alert('Badge updated successfully!')
        } else {
            alert(`Badge update failed: ${error || 'Unknown error'}`)
        }
    }

    const onClickDelete = async () => {
        if (!badgeId) return
        if (!window.confirm('Are you sure you want to deactivate this badge?')) return

        const {data, error} = await deleteBadge(badgeId)
        if (data) {
            alert('Badge deactivated successfully!')
            goBadge()
        } else {
            alert(`Failed: ${error || 'Unknown error'}`)
        }
    }

    const onClickGrant = async () => {
        if (!badgeId) return
        const userIds = parseUserIds()
        if (userIds.length === 0) {
            alert('Please enter at least one User ID')
            return
        }

        const {data, error} = await grantBadge(badgeId, userIds)
        if (data) {
            alert(`Badge granted to ${data.granted ?? userIds.length} user(s)`)
            setUserIdsText('')
        } else {
            alert(`Grant failed: ${error || 'Unknown error'}`)
        }
    }

    const onClickRevoke = async () => {
        if (!badgeId) return
        const userIds = parseUserIds()
        if (userIds.length === 0) {
            alert('Please enter at least one User ID')
            return
        }
        if (!window.confirm(`Revoke badge from ${userIds.length} user(s)?`)) return

        const {data, error} = await revokeBadge(badgeId, userIds)
        if (data) {
            alert(`Badge revoked from ${data.revoked ?? userIds.length} user(s)`)
            setUserIdsText('')
        } else {
            alert(`Revoke failed: ${error || 'Unknown error'}`)
        }
    }

    return (
        <PageLayout title={'Badge Detail'}>
            <div className={'max-w-[800px] mx-auto space-y-[20px]'}>
                <div className={'bg-white rounded-[10px] shadow-normal p-[30px] space-y-[25px]'}>
                    <Text className={'text-h3 text-center'}>Badge Info</Text>

                    <div>
                        <Text className={'text-body3 text-font02'}>Code</Text>
                        <Text className={'text-caption text-font03'}>
                            Unique slug for frontend identification (e.g. asset_1m, wolf_pack). Lowercase + underscores only.
                        </Text>
                        <UnderLineInput
                            value={code}
                            onChanged={text => setCode(text.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                        />
                    </div>

                    <div className={'space-y-[3px]'}>
                        <Text className={'text-body3 text-font02 text-center'}>Category</Text>
                        <WizSelectButton
                            className={'mx-auto'}
                            data={categoryOptions}
                            onChange={e => setCategory(e.id)}
                            activeValue={categoryOptions.find(o => o.id === category)}
                        />
                    </div>

                    <div className={'space-y-[3px]'}>
                        <Text className={'text-body3 text-font02 text-center'}>Type</Text>
                        <WizSelectButton
                            className={'mx-auto'}
                            data={typeOptions}
                            onChange={e => setType(e.id)}
                            activeValue={typeOptions.find(o => o.id === type)}
                        />
                    </div>

                    <div>
                        <Text className={'text-body3 text-font02'}>Sort Order</Text>
                        <Text className={'text-caption text-font03'}>
                            Badge list display order. Lower number = shown first.
                        </Text>
                        <UnderLineInput value={sortOrder} onChanged={text => setSortOrder(Number(text))} />
                    </div>

                    <div>
                        <Text className={'text-body3 text-font02'}>Remark</Text>
                        <Text className={'text-caption text-font03'}>
                            Internal note for admins. Not visible to users.
                        </Text>
                        <UnderLineInput value={remark} onChanged={text => setRemark(text)} />
                    </div>
                </div>

                <div className={'bg-white rounded-[10px] shadow-normal p-[30px] space-y-[25px]'}>
                    <Text className={'text-h3 text-center'}>Localization</Text>

                    <div className={'grid grid-cols-2 gap-[20px] sm:grid-cols-1'}>
                        <div className={'space-y-[15px]'}>
                            <Text className={'text-ti2 text-center'}>English</Text>
                            <div>
                                <Text className={'text-body3 text-font02'}>Name (EN)</Text>
                                <UnderLineInput value={nameEn} onChanged={text => setNameEn(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Description (EN)</Text>
                                <UnderLineInput value={descEn} onChanged={text => setDescEn(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Criteria (EN)</Text>
                                <UnderLineInput value={criteriaEn} onChanged={text => setCriteriaEn(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Requirement (EN)</Text>
                                <UnderLineInput value={requirementEn} onChanged={text => setRequirementEn(text)} />
                            </div>
                        </div>
                        <div className={'space-y-[15px]'}>
                            <Text className={'text-ti2 text-center'}>Korean</Text>
                            <div>
                                <Text className={'text-body3 text-font02'}>Name (KO)</Text>
                                <UnderLineInput value={nameKo} onChanged={text => setNameKo(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Description (KO)</Text>
                                <UnderLineInput value={descKo} onChanged={text => setDescKo(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Criteria (KO)</Text>
                                <UnderLineInput value={criteriaKo} onChanged={text => setCriteriaKo(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Requirement (KO)</Text>
                                <UnderLineInput value={requirementKo} onChanged={text => setRequirementKo(text)} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={'bg-white rounded-[10px] shadow-normal p-[30px] space-y-[25px]'}>
                    <Text className={'text-h3 text-center'}>Images</Text>

                    <div className={'grid grid-cols-3 gap-[20px] sm:grid-cols-1'}>
                        <div>
                            <Text className={'text-body3 text-font02 mb-[10px]'}>Badge Image</Text>
                            <div
                                onClick={() => imageRef?.current?.click()}
                                className={
                                    'border border-line02 rounded-[10px] overflow-hidden cursor-pointer h-[150px] flex items-center justify-center bg-line04'
                                }>
                                {image ? (
                                    <WizImage
                                        src={`${image}?type=webp&width=400`}
                                        className={'max-h-[140px] object-contain'}
                                    />
                                ) : (
                                    <Text className={'text-font03'}>Click to upload</Text>
                                )}
                            </div>
                            <input
                                type={'file'}
                                accept={'image/*'}
                                onChange={e => onChangeImage(e, setImage)}
                                ref={imageRef}
                                className={'hidden'}
                            />
                        </div>
                        <div>
                            <Text className={'text-body3 text-font02 mb-[10px]'}>Badge Mini Image</Text>
                            <div
                                onClick={() => imageMiniRef?.current?.click()}
                                className={
                                    'border border-line02 rounded-[10px] overflow-hidden cursor-pointer h-[150px] flex items-center justify-center bg-line04'
                                }>
                                {imageMini ? (
                                    <WizImage
                                        src={`${imageMini}?type=webp&width=200`}
                                        className={'max-h-[140px] object-contain'}
                                    />
                                ) : (
                                    <Text className={'text-font03'}>Click to upload</Text>
                                )}
                            </div>
                            <input
                                type={'file'}
                                accept={'image/*'}
                                onChange={e => onChangeImage(e, setImageMini)}
                                ref={imageMiniRef}
                                className={'hidden'}
                            />
                        </div>
                        <div>
                            <Text className={'text-body3 text-font02 mb-[10px]'}>Badge Disabled Image</Text>
                            <div
                                onClick={() => imageDisabledRef?.current?.click()}
                                className={
                                    'border border-line02 rounded-[10px] overflow-hidden cursor-pointer h-[150px] flex items-center justify-center bg-line04'
                                }>
                                {imageDisabled ? (
                                    <WizImage
                                        src={`${imageDisabled}?type=webp&width=400`}
                                        className={'max-h-[140px] object-contain'}
                                    />
                                ) : (
                                    <Text className={'text-font03'}>Click to upload</Text>
                                )}
                            </div>
                            <input
                                type={'file'}
                                accept={'image/*'}
                                onChange={e => onChangeImage(e, setImageDisabled)}
                                ref={imageDisabledRef}
                                className={'hidden'}
                            />
                        </div>
                    </div>
                </div>

                <div className={'flex justify-center gap-x-[10px]'}>
                    <WizButton type={'primary'} size={'big'} text={'Save Changes'} onClick={onClickSave} />
                    <WizButton type={'red'} size={'big'} text={'Deactivate'} onClick={onClickDelete} />
                </div>

                <div className={'bg-white rounded-[10px] shadow-normal p-[30px] space-y-[15px]'}>
                    <Text className={'text-h3 text-center'}>Grant / Revoke Badge</Text>
                    <div>
                        <Text className={'text-body3 text-font02 mb-[5px]'}>
                            User IDs (comma or newline separated)
                        </Text>
                        <textarea
                            value={userIdsText}
                            onChange={e => setUserIdsText(e.target.value)}
                            placeholder={'user_id_1, user_id_2\nuser_id_3'}
                            className={
                                'w-full h-[120px] border border-line02 rounded-[10px] p-[15px] text-body2 resize-none focus:outline-none focus:border-sub_blue'
                            }
                        />
                    </div>
                    <div className={'flex justify-center gap-x-[10px]'}>
                        <WizButton type={'primary'} text={'Grant Badge'} onClick={onClickGrant} />
                        <WizButton type={'red'} text={'Revoke Badge'} onClick={onClickRevoke} />
                    </div>
                </div>

                <div className={'h-[40px]'} />
            </div>
        </PageLayout>
    )
}

export default BadgeDetailPage
